if (typeof window.ProductComparison === "undefined") {
  window.ProductComparison = class ProductComparison {
    constructor() {
      this.storageKey = "compare_products";
      this.maxProducts = 30;
      this.selectors = {
        toggle: "[data-compare-toggle]",
        container: "[data-compare-container]",
        tableBody: "[data-compare-table-body]",
        emptyState: "[data-compare-empty]",
        countBadge: "[data-compare-count]",
        removeAll: "[data-compare-remove-all]",
        headerLink: "[data-compare-header-link]",
        popup: "#compare-popup",
        popupName: ".compare-product-name",
        popupClose: ".compare-popup-close",
      };

      this.init();
    }

    init() {
      this.bindEvents();
      this.updateButtonStates();

      // Check if we are on the comparison page
      if (document.querySelector(this.selectors.container)) {
        this.renderComparisonTable();
      }
    }

    getProducts() {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || "[]");
      // Migration: Convert old string array to object array
      const products = stored
        .map((item) => {
          if (typeof item === "string") {
            return { handle: item, variantId: null };
          }
          return item;
        })
        .filter((item) => item.handle && item.handle.trim() !== ""); // Filter out invalid items

      return products;
    }

    setProducts(products) {
      localStorage.setItem(this.storageKey, JSON.stringify(products));
      this.dispatchUpdate();
    }

    addProduct(handle, variantId = null) {
      const products = this.getProducts();
      // Check if already exists
      const exists = products.some(
        (p) => p.handle === handle && p.variantId == variantId
      );
      if (exists) return;

      if (products.length >= this.maxProducts) {
        alert(`You can only compare up to ${this.maxProducts} products.`);
        return;
      }

      products.push({ handle, variantId });
      this.setProducts(products);
    }

    removeProduct(handle, variantId = null) {
      let products = this.getProducts();
      products = products.filter(
        (p) => !(p.handle === handle && p.variantId == variantId)
      );
      this.setProducts(products);
    }

    toggleProduct(handle, variantId = null, title = null) {
      const products = this.getProducts();
      const exists = products.some(
        (p) => p.handle === handle && p.variantId == variantId
      );

      if (exists) {
        this.removeProduct(handle, variantId);
      } else {
        this.addProduct(handle, variantId);
        if (title) {
          this.showPopup(title);
        }
      }
    }

    showPopup(title) {
      const popup = document.querySelector(this.selectors.popup);
      const popupName = popup?.querySelector(this.selectors.popupName);

      if (!popup || !popupName) return;

      popupName.textContent = title;
      popup.classList.remove("hidden");

      // Add visible class after a small delay to trigger transition
      requestAnimationFrame(() => {
        popup.classList.add("visible");
      });

      // Auto hide after 5 seconds
      if (this.popupTimeout) clearTimeout(this.popupTimeout);
      this.popupTimeout = setTimeout(() => {
        this.closePopup();
      }, 5000);
    }

    closePopup() {
      const popup = document.querySelector(this.selectors.popup);
      if (!popup) return;

      popup.classList.remove("visible");

      // Wait for transition to finish before hiding
      setTimeout(() => {
        popup.classList.add("hidden");
      }, 300); // Match CSS transition duration
    }

    bindEvents() {
      document.addEventListener("click", (e) => {
        const toggle = e.target.closest(this.selectors.toggle);
        if (toggle) {
          e.preventDefault();
          const handle = toggle.dataset.productHandle;
          const title = toggle.dataset.productTitle;
          const variantId = toggle.dataset.variantId || null;
          this.toggleProduct(handle, variantId, title);
        }

        const removeAll = e.target.closest(this.selectors.removeAll);
        if (removeAll) {
          e.preventDefault();
          this.setProducts([]);
        }

        const popupClose = e.target.closest(this.selectors.popupClose);
        if (popupClose) {
          e.preventDefault();
          this.closePopup();
        }
      });

      window.addEventListener("compare:updated", () => {
        this.updateButtonStates();
        if (document.querySelector(this.selectors.container)) {
          this.renderComparisonTable();
        }
      });
    }

    updateButtonStates() {
      const products = this.getProducts();
      const toggles = document.querySelectorAll(this.selectors.toggle);

      toggles.forEach((toggle) => {
        const handle = toggle.dataset.productHandle;
        const variantId = toggle.dataset.variantId || null;

        const isActive = products.some(
          (p) => p.handle === handle && p.variantId == variantId
        );

        toggle.classList.toggle("active", isActive);
        const textSpan = toggle.querySelector(".compare-text");
        if (textSpan) {
          textSpan.textContent = isActive ? "Remove" : "Compare";
        }
        toggle.setAttribute("aria-pressed", isActive);
      });

      // Update count badges if any
      const badges = document.querySelectorAll(this.selectors.countBadge);
      badges.forEach((badge) => {
        badge.textContent = products.length;
        badge.hidden = products.length === 0;
      });

      // Update header link visibility
      const headerLinks = document.querySelectorAll(this.selectors.headerLink);
      headerLinks.forEach((link) => {
        if (products.length > 0) {
          link.classList.remove("hidden");
        } else {
          link.classList.add("hidden");
        }
      });
    }

    dispatchUpdate() {
      window.dispatchEvent(new CustomEvent("compare:updated"));
    }

    async renderComparisonTable() {
      const products = this.getProducts();
      const container = document.querySelector(this.selectors.container);
      const emptyState = document.querySelector(this.selectors.emptyState);
      const gridContainer = document.querySelector("[data-compare-grid]");

      if (products.length === 0) {
        if (container) container.classList.add("hidden");
        if (emptyState) emptyState.classList.remove("hidden");
        return;
      }

      if (container) container.classList.remove("hidden");
      if (emptyState) emptyState.classList.add("hidden");

      if (!gridContainer) return;

      // Fetch data
      const fetches = products.map(({ handle, variantId }) => {
        let url = `/products/${handle}?view=compare`;
        if (variantId) {
          url += `&variant=${variantId}`;
        }

        return fetch(url)
          .then((res) => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.text();
          })
          .then((text) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, "text/html");
            const section = doc.querySelector(".shopify-section");
            if (!section) {
              return null;
            }
            return {
              handle,
              variantId,
              html: section.innerHTML,
            };
          })
          .catch((err) => {
            console.error(
              `ProductComparison: Failed to fetch product ${handle}`,
              err
            );
            return null;
          });
      });

      const results = await Promise.all(fetches);
      const validResults = results.filter((r) => r !== null);

      // Update CSS Variable for column count on all rows
      const rows = gridContainer.querySelectorAll(".compare-row");
      rows.forEach((row) => {
        row.style.setProperty("--product-count", validResults.length);
      });

      // Map data by field
      const fieldMap = new Map();
      validResults.forEach(({ handle, variantId, html }) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        // Update remove button in the fetched HTML to include variant ID
        const removeBtn = tempDiv.querySelector("[data-compare-toggle]");
        if (removeBtn && variantId) {
          removeBtn.dataset.variantId = variantId;
        }

        const cells = tempDiv.querySelectorAll("[data-compare-field]");
        cells.forEach((cell) => {
          const field = cell.dataset.compareField;
          if (!fieldMap.has(field)) {
            fieldMap.set(field, []);
          }
          fieldMap.get(field).push({ handle, variantId, html: cell.innerHTML });
        });
      });

      // Populate each row
      rows.forEach((row) => {
        const fieldName = row.dataset.rowId;
        if (!fieldName) return;

        const dataCells = fieldMap.get(fieldName) || [];

        // Clear existing product cells (anything that is NOT a label)
        const existingCells = row.querySelectorAll(
          ".compare-cell:not(.compare-label)"
        );
        existingCells.forEach((c) => c.remove());

        dataCells.forEach((data) => {
          const div = document.createElement("div");
          div.className = "compare-cell";
          div.innerHTML = data.html;
          row.appendChild(div);
        });
      });
    }
  };
}

document.addEventListener("DOMContentLoaded", () => {
  if (!window.productComparison) {
    window.productComparison = new window.ProductComparison();
  }
});
